import { useEffect, useState } from 'react';

import { useContentfulClient } from '../useContentfulClient';
import type { ContentfulContentTypeEntries } from './useContentfulContentTypeEntries.types';
import {
  extractContentTypeEntryData,
  generateIncludedTagsParamEntry,
} from './useContentfulContentTypeEntries.utils';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns Contentful entries for the provided content type.
 *
 * Additionally, returns `fetching` and `error` states.
 *
 * @example
 *
 * const { data, fetching, error } = useContentfulContentTypeEntries({
 *   contentType: 'contentfulContentTypeID',
 * });
 * const [firstEntry, secondEntry] = data ?? [];
 *
 * const { fields, tags } = firstEntry ?? {};
 * const { image, bodyText, headingText } = fields ?? {};
 * const hasSuperTag = tags?.has('SUPER');
 *
 * const { headingText } = secondEntry.fields ?? {};
 *
 * @see {@link https://www.contentful.com/developers/docs/javascript/tutorials/using-js-cda-sdk/#retrieve-all-entries-of-a-space Contentful Docs}
 */
export const useContentfulContentTypeEntries = <ContentTypeFields>(
  query: ContentfulQuery,
) => {
  const contentfulClient = useContentfulClient();

  const {
    limit,
    order = 'updated-at',
    include,
    contentType,
    tags = [],
    pause = false,
  } = query;
  const queryOrder =
    order === 'updated-at' ? '-sys.updatedAt' : '-sys.createdAt';
  const tagsString = tags.toString();

  // ─── State ───────────────────────────────────────────────────────────

  const [data, setData] = useState<
    ContentfulContentTypeEntries<ContentTypeFields> | undefined
  >();
  const [error, setError] = useState<unknown>();
  const [fetching, setFetching] = useState(!pause);

  // ─────────────────────────────────────────────────────────────────────

  useEffect(
    () => {
      if (pause) {
        setFetching(false);

        return;
      }

      // ─── "No Client" case ────────────────────────────────────────────────

      if (!contentfulClient) {
        setData(undefined);
        setFetching(false);
        setError(new Error('Contentful client was not found!'));

        return;
      }

      // reset state before change
      setFetching(true);
      setError(undefined);

      // ─────────────────────────────────────────────────────────────────────

      (async () => {
        try {
          const queryInput = {
            content_type: contentType,
            include,
            limit,
            order: queryOrder,
            ...generateIncludedTagsParamEntry(tagsString),
          };

          const resolvedContent =
            await contentfulClient.getEntries<ContentTypeFields>(queryInput);
          const resolvedContentItems = resolvedContent?.items ?? [];

          // ─── "No items" case ─────────────────────────────────────────────────

          if (resolvedContentItems.length === 0) {
            setData(undefined);

            return;
          }

          // ─────────────────────────────────────────────────────────────────────

          setData(resolvedContentItems.map(extractContentTypeEntryData));
        } catch (contentfulError: unknown) {
          setData(undefined);
          setError(contentfulError);
        } finally {
          setFetching(false);
        }
      })();
    },
    // NOTE: Use only primitive and/or memoized non-primitive values in this list to avoid
    //       an infinite data fetching loop.
    [
      contentType,
      contentfulClient,
      include,
      limit,
      order,
      pause,
      queryOrder,
      tagsString,
    ],
  );

  return { data, error, fetching };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ContentfulQuery = {
  readonly contentType: string;
  readonly include?: number;
  readonly limit?: number;
  readonly order?: 'created-at' | 'updated-at';
  readonly pause?: boolean;
  readonly tags?: readonly string[];
};
